import { useForm, FieldData, TextField, Select } from 'vopay';
import { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

export type PartnerCreationFormValues = {
  PartnerName: string;
  PartnerAccountID: string;
  Country: string;
  EmailAddress: string;
};

export type SerializedPartnerCreationValues = {
  PartnerName: string;
  PartnerAccountID?: string;
  Country: string;
  EmailAddress: string;
};

const initialValues: PartnerCreationFormValues = {
  PartnerName: '',
  PartnerAccountID: '',
  Country: '',
  EmailAddress: '',
};

type UsePartnerCreationFormConfigProps = {
  onSubmit: (values: SerializedPartnerCreationValues) => Promise<void>;
  isReviewing: boolean;
};

export default function usePartnerCreationFormConfig({ onSubmit, isReviewing }: UsePartnerCreationFormConfigProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.Create' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  // Partner creation is restricted to Canada and the United States. Values are the API values — never translated.
  const countryOptions = [
    { value: 'Canada', label: tGlobal('Countries.CA') },
    { value: 'United States', label: tGlobal('Countries.USLong') },
  ];

  const businessInfoConfig = useMemo<FieldData<PartnerCreationFormValues>[]>(
    () => [
      {
        label: () => t('partnerName', { defaultValue: 'Partner Name' }),
        fieldName: 'PartnerName',
        getInputState: () => 'required',
        showField: () => true,
        renderField: ({ getFormikData }) => (
          <TextField
            {...getFormikData<PartnerCreationFormValues['PartnerName']>({})}
            variant="outlineShadow"
            placeholder={t('partnerNamePlaceholder', { defaultValue: 'Enter' })}
          />
        ),
        validate: () =>
          Yup.string()
            .label(t('partnerName', { defaultValue: 'Partner Name' }))
            .required(),
      },
      {
        label: () => t('partnerAccountId', { defaultValue: 'Partner Account ID' }),
        fieldName: 'PartnerAccountID',
        getInputState: () => 'optional',
        showField: () => true,
        renderField: ({ getFormikData }) => (
          <TextField
            {...getFormikData<PartnerCreationFormValues['PartnerAccountID']>({})}
            variant="outlineShadow"
            placeholder={t('partnerAccountIdPlaceholder', { defaultValue: 'Enter' })}
          />
        ),
      },
      {
        label: () => t('country', { defaultValue: 'Country' }),
        fieldName: 'Country',
        getInputState: () => 'required',
        showField: () => true,
        renderField: ({ getFormikData }) => (
          <Select
            {...getFormikData<PartnerCreationFormValues['Country']>({})}
            version="2"
            showClear
            filterable
            options={countryOptions}
            placeholder={t('countryPlaceholder', { defaultValue: 'Select' })}
          />
        ),
        validate: () =>
          Yup.string()
            .label(t('country', { defaultValue: 'Country' }))
            .required(),
      },
      {
        label: () => t('emailAddress', { defaultValue: 'Email Address' }),
        fieldName: 'EmailAddress',
        getInputState: () => 'required',
        showField: () => true,
        renderField: ({ getFormikData }) => (
          <TextField
            errorFitContent
            {...getFormikData<PartnerCreationFormValues['EmailAddress']>({})}
            variant="outlineShadow"
            placeholder={t('emailAddressPlaceholder', { defaultValue: 'name@domain.com' })}
          />
        ),
        validate: () =>
          Yup.string()
            .label(t('emailAddress', { defaultValue: 'Email Address' }))
            .email()
            .required(),
      },
    ],
    [],
  );

  const { ctx, renderFormElements } = useForm<PartnerCreationFormValues, SerializedPartnerCreationValues>({
    initialValues,
    config: businessInfoConfig,
    onSubmit,
    isReviewing,
  });

  return { ctx, renderFormElements, businessInfoConfig };
}
